import React, { useMemo } from 'react';
import { WholeCandleAnalysis } from '@shared/types';
import './ConfidenceInfoOverlay.css';

interface ConfidenceInfoOverlayProps {
  analysis: WholeCandleAnalysis | null;
  isLoading?: boolean;
}

// Generate human-readable commentary for a factor
const generateFactorCommentary = (
  factor: { name: string; price: number; positionPct: number; zone: string; contribution: number },
  signalType: 'bullish' | 'bearish' | null,
  close: number
): string | null => {
  const isBullish = signalType === 'bullish';
  const isAbove = close > factor.price;

  // EMA commentary
  if (factor.name.startsWith('EMA')) {
    const emaNum = factor.name.replace('EMA', '');
    if (factor.zone === 'low') {
      if (isAbove && isBullish) return `Broke ${emaNum}E`;
      if (!isAbove && !isBullish) return `Rejected ${emaNum}E`;
      if (!isAbove && isBullish) return `${emaNum}E resistance`;
      if (isAbove && !isBullish) return `${emaNum}E support`;
    } else if (factor.zone === 'mid') {
      return isAbove ? `Above ${emaNum}E` : `Below ${emaNum}E`;
    }
    return null;
  }

  // VWAP commentary
  if (factor.name === 'VWAP') {
    if (factor.zone === 'low') {
      if (isAbove && isBullish) return 'Broke VWAP';
      if (!isAbove && !isBullish) return 'Under VWAP';
      if (!isAbove && isBullish) return 'VWAP resistance';
      if (isAbove && !isBullish) return 'VWAP support';
    } else if (factor.zone === 'mid') {
      return isAbove ? 'Above VWAP' : 'Below VWAP';
    }
    return null;
  }

  // Fib commentary with level + meaning
  if (factor.name.startsWith('Fib')) {
    const fibLevel = factor.name.replace('Fib ', '');
    // Map Fib levels to meaningful descriptions
    const fibMeaning: Record<string, string> = {
      '38.2%': 'pullback',      // Strong-trend continuation pullback
      '50%': 'fair value',      // Institutional "fair value" line
      '61.8%': 'golden',        // Main re-entry / reversal zone
      '70.5%': 'stop-hunt',     // Stop-hunt pocket
      '78.6%': 'last chance',   // Last chance before trend breaks
    };
    const meaning = fibMeaning[fibLevel] || '';
    const label = meaning ? `${fibLevel} ${meaning}` : fibLevel;

    if (factor.zone === 'low') {
      if (isAbove && isBullish) return `Broke ${label}`;
      if (!isAbove && !isBullish) return `Under ${label}`;
      return `At ${label}`;
    } else if (factor.zone === 'mid') {
      return isAbove ? `Above ${label}` : `Below ${label}`;
    }
    return null;
  }

  // HOD/LOD commentary
  if (factor.name === 'HOD') {
    if (factor.zone === 'low') return 'Near HOD';
    if (factor.zone === 'mid') return 'Room to HOD';
    return null;
  }
  if (factor.name === 'LOD') {
    if (factor.zone === 'low') return 'Near LOD';
    if (factor.zone === 'mid') return 'Room to LOD';
    return null;
  }

  return null;
};

const ConfidenceInfoOverlay: React.FC<ConfidenceInfoOverlayProps> = React.memo(({ analysis, isLoading }) => {
  const displayData = useMemo(() => {
    if (!analysis) return null;

    const hasConfidence = analysis.confidence !== undefined && analysis.confidence !== null;
    if (!hasConfidence) return null;

    const confidenceScore = Math.round(analysis.confidence!);
    const confidenceClass = analysis.confidence! >= 70 ? 'high' : analysis.confidence! >= 50 ? 'medium' : 'low';
    const confidenceFactors = analysis.confidenceFactors || [];

    // Group factors by type for display
    const emaFactors = confidenceFactors.filter(f => f.name.startsWith('EMA'));
    const vwapFactor = confidenceFactors.find(f => f.name === 'VWAP');
    const fibFactors = confidenceFactors.filter(f => f.name.startsWith('Fib'));
    const hodLodFactors = confidenceFactors.filter(f => f.name === 'HOD' || f.name === 'LOD');

    // Generate commentary for the most relevant factors (those in low/mid zones)
    const commentary: string[] = [];

    // Add EMA commentary (most important ones first)
    for (const f of emaFactors) {
      const comment = generateFactorCommentary(f, analysis.signalType, analysis.close);
      if (comment) commentary.push(comment);
    }

    // Add VWAP commentary
    if (vwapFactor) {
      const comment = generateFactorCommentary(vwapFactor, analysis.signalType, analysis.close);
      if (comment) commentary.push(comment);
    }

    // Add Fib commentary (limit to avoid clutter)
    for (const f of fibFactors.slice(0, 2)) {
      const comment = generateFactorCommentary(f, analysis.signalType, analysis.close);
      if (comment) commentary.push(comment);
    }

    // Add HOD/LOD commentary
    for (const f of hodLodFactors) {
      const comment = generateFactorCommentary(f, analysis.signalType, analysis.close);
      if (comment) commentary.push(comment);
    }

    // Add stack/alignment bonuses
    if (analysis.emaStackAligned) {
      commentary.push(analysis.signalType === 'bullish' ? 'EMAs stacked bullish' : 'EMAs stacked bearish');
    }

    // Add level density warning
    if (analysis.levelDensity != null && analysis.levelDensity >= 3) {
      commentary.push('Congestion zone');
    }

    // Add next level info
    if (analysis.nextLevelDistance != null && analysis.nextLevelDistance < 10) {
      const direction = analysis.signalType === 'bullish' ? 'resistance' : 'support';
      commentary.push(`${direction} ${analysis.nextLevelDistance.toFixed(0)}pt`);
    }

    return {
      confidenceScore,
      confidenceClass,
      confidenceFactors,
      emaFactors,
      vwapFactor,
      fibFactors,
      hodLodFactors,
      emaStackAligned: analysis.emaStackAligned,
      vwapAligned: analysis.vwapAligned,
      levelDensity: analysis.levelDensity,
      nextLevelDistance: analysis.nextLevelDistance,
      commentary: commentary.slice(0, 4), // Limit to 4 most relevant
    };
  }, [analysis]);

  if (isLoading) {
    return (
      <div className="confidence-info-overlay">
        <div className="confidence-info-content">
          <span className="confidence-info-loading">...</span>
        </div>
      </div>
    );
  }

  if (!analysis || !displayData) {
    return (
      <div className="confidence-info-overlay">
        <div className="confidence-info-content">
          <span className="confidence-info-label">Conf</span>
          <span className="confidence-info-value neutral">–</span>
        </div>
      </div>
    );
  }

  return (
    <div className="confidence-info-overlay">
      <div className="confidence-info-content">
        {/* Confidence Score */}
        <div className="confidence-score-section">
          <span className="confidence-info-label">Conf</span>
          <span className={`confidence-score ${displayData.confidenceClass}`}>
            {displayData.confidenceScore}
          </span>
        </div>

        {/* Commentary - human readable explanation */}
        {displayData.commentary.length > 0 && (
          <div className="confidence-commentary">
            {displayData.commentary.map((text, i) => (
              <span key={i} className="confidence-comment">{text}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

export default ConfidenceInfoOverlay;
